import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { AuthService, NotifyService, PayrollService, UrlHelperService } from '@app/services';
import { ShowView } from '@common/views';
import { environment } from '@env/environment';
import { Roe, RoeState } from '@models/payroll/roe.model';
import { InsurableEarning } from '@payroll/interfaces';
import { saveAs } from 'file-saver';

@Component({
    templateUrl: './roe-show.template.html',
    styleUrls: ['./roe-show.style.scss'],
})
export class RoeShowView extends ShowView {
    @ViewChild('confirmDeleteDialog', { static: true }) confirmDeleteDialog!: ConfirmationDialogComponent;
    isLoading = true;
    roe: Roe;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private urlHelper: UrlHelperService,
        private auth: AuthService,
        private payrollService: PayrollService
    ) {
        super();

        this.roe = this.route.snapshot.data.models.get('roe');
        this.isLoading = false;
    }

    getStatusBadgeType(state: RoeState): string {
        switch (state) {
            case 'open':
                return 'default';
            case 'pending':
            case 'processing':
                return 'warning';
            case 'submitted':
                return 'success';
            case 'failed':
                return 'danger';
            // We generally shouldn't see these in the frontend
            case 'nonhumi':
                return '';
        }
    }

    getStatusBadgeText(state: string): string {
        return state === 'processing' ? 'pending' : state;
    }

    getInsurableAmountByPayPeriodNumber(payPeriodNumber: number): InsurableEarning | undefined {
        return this.roe.insurableEarnings.find((insurableEarning) => {
            return insurableEarning.payPeriodNumber === payPeriodNumber;
        });
    }

    onCancel(): void {}
    onSave(): void {}

    async onDownload(): Promise<void> {
        const path = this.downloadPath;

        this.urlHelper.getBlob(path).subscribe((data) => {
            if (!data) {
                this.notify.error('No ROE file available');

                return;
            }

            saveAs(data, `${this.roe.employeeFullName()}.xml`);
        });
    }

    onSubmit(): void {
        this.isLoading = true;

        this.payrollService
            .submitRoe(this.auth.company, this.roe)
            .then((roe: Roe) => {
                this.notify.success('ROE submitted successfully.');
                this.roe = roe;
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err.message || 'Could not submit ROE.');
                this.isLoading = false;
            });
    }

    onDelete(): void {
        this.confirmDeleteDialog.open().then((confirm) => {
            if (confirm) {
                this.isLoading = true;
                this.roe
                    .delete()
                    .then(() => {
                        this.notify.success('Success');
                        this.router.navigate(['../'], { relativeTo: this.route });
                    })
                    .catch((err) => {
                        this.isLoading = false;
                        this.notify.error(err.message);
                    });
            }
        });
    }

    onEdit(roe: Roe): void {
        this.router.navigate(['/payroll', 'records', 'roes', roe.id, 'edit']);
    }

    private get downloadPath(): string {
        return (
            `${environment.api}/${Roe.version}/` +
            Roe.resource.replace(':company', `${this.auth.company.id}`) +
            `/${this.roe.id}` +
            `/download`
        );
    }
}
